/**
 * WHAT: Cassandra user access report with connection-scoped filtering and privilege detail modals.
 * WHY:  Cassandra uses a distinct permission model (superuser flag, keyspace-level grants); this
 *       report lets compliance teams review Cassandra-specific access across clusters to support
 *       attestation and identify over-privileged accounts.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import { useSafeTimeout } from '../hooks';
import { WideModal } from './common';
import './MySQLUserAccessReport.css'; // Reuse MySQL styles

/**
 * WHAT: Renders a per-connection Cassandra user access report with superuser and privilege level filters.
 * WHY:  Allows Cassandra administrators and auditors to examine user privileges per cluster connection,
 *       filter by superuser status, and drill into keyspace-level grants for compliance evidence.
 */
const CassandraUserAccessReport = () => {
  const [connections, setConnections] = useState([]);
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [selectedConnection, setSelectedConnection] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collectionDates, setCollectionDates] = useState([]);
  const [filters, setFilters] = useState({
    collection_date: '',
    privilege_level: '',
    is_superuser: '',
    user_type: '', // Internal | Individual | Application (blank = all)
    search: ''
  });

  // Cassandra Privilege-Mappings certification status — used by the warning
  // banner above the cards. Hidden when 0 unassigned + 0 uncertified.
  const [certStatus, setCertStatus] = useState(null);
  // Privilege-level drill-down modal state. Populated when a user clicks their
  // Privilege Level badge in the table. Holds the username + breakdown payload.
  const [levelBreakdown, setLevelBreakdown] = useState(null);

  // Pagination — server-side. Backend returns total_count/total_pages for
  // the current filter set; the client tracks current page + size only.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modal state for showing privilege details
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  // Privilege-level filter options driven by the Privilege Matrix per-tech config.
  // Disabling a tier for Cassandra in the matrix removes it from this dropdown.
  const [activeTiers, setActiveTiers] = useState([]);

  // Safe timeout hook for delayed operations
  const { setSafeTimeout } = useSafeTimeout();

  useEffect(() => {
    fetchCassandraConnections();
    fetchActiveTiers();
    fetchCertificationStatus();
  }, []);

  const fetchActiveTiers = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-matrix/cassandra/active-tiers/'));
      if (response.data?.success) {
        setActiveTiers(response.data.tiers || []);
      }
    } catch (err) {
      console.error('Failed to fetch active tiers for Cassandra:', err);
    }
  };

  const fetchCertificationStatus = async () => {
    try {
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/check-certification/'),
        { params: { database_type: 'CASSANDRA' } }
      );
      if (response.data?.status === 'success') {
        setCertStatus(response.data);
      }
    } catch (e) {
      setCertStatus(null);
    }
  };

  // Fetch and open the privilege-level breakdown for one Cassandra user. The
  // backend walks the user's stored all_permissions, levels each grant via
  // cassandra_privilege_mapping_reference, marks the MAX-driving component(s),
  // and adds a synthetic Superuser row when applicable.
  const openLevelBreakdown = async (user) => {
    setLevelBreakdown({ name: user.username, loading: true, data: null });
    try {
      const resp = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/cassandra-user-component-levels/'),
        {
          params: {
            database_type: 'CASSANDRA',
            connection_id: selectedConnection,
            username: user.username,
          },
        }
      );
      setLevelBreakdown({ name: user.username, loading: false, data: resp.data });
    } catch (e) {
      setLevelBreakdown({
        name: user.username, loading: false, data: null,
        error: e.response?.data?.message || e.message,
      });
    }
  };

  const fetchCassandraConnections = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('cassandra/source-connections/'));
      const cassandraConns = response.data.connections || [];
      // Map id to connection_id for consistency
      const mappedConns = cassandraConns.map(conn => ({
        ...conn,
        connection_id: conn.id,
        database_type: 'CASSANDRA'
      }));
      setConnections(mappedConns);
      if (mappedConns.length > 0 && !selectedConnection) {
        setSelectedConnection(mappedConns[0].connection_id);
        fetchUsers(mappedConns[0].connection_id);
      }
    } catch (err) {
      console.error('Error fetching Cassandra connections:', err);
      setError('Failed to fetch Cassandra connections');
    }
  };

  const fetchCollectionDates = async (connId = '') => {
    if (!connId) { setCollectionDates([]); return; }
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint(`cassandra-access/collection-dates/?connection_id=${connId}`));
      setCollectionDates(response.data.collection_dates || []);
    } catch (err) {
      console.error('Error fetching collection dates:', err);
      setCollectionDates([]);
    }
  };

  const fetchUsers = async (connId = selectedConnection, overridePage = null, overrideSize = null, overrideFilters = null) => {
    if (!connId) return;

    setLoading(true);
    setError(null);
    try {
      const p = overridePage ?? currentPage;
      const s = overrideSize ?? pageSize;
      // Prefer explicit overrideFilters so callers driven by onChange events
      // can send the just-changed value WITHOUT waiting for setFilters to commit.
      const f = overrideFilters || filters;
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('cassandra-access/report/'), {
        connection_id: connId,
        page: p,
        page_size: s,
        filters: {
          ...(f.collection_date && { collection_date: f.collection_date }),
          ...(f.privilege_level && { privilege_level: parseInt(f.privilege_level) }),
          ...(f.is_superuser !== '' && { is_superuser: f.is_superuser === 'true' }),
          ...(f.user_type && { user_type: f.user_type }),
          ...(f.search && { search: f.search })
        }
      });
      setUsers(response.data.users || []);
      setStatistics(response.data.statistics || {});
      setTotalCount(response.data.total_count || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (err) {
      // Clear the report so a failed load (e.g. an uncollected/removed DB) shows
      // an empty report — NOT the previously-selected database's stale rows.
      setUsers([]);
      setStatistics({});
      setTotalCount(0);
      setTotalPages(0);
      setError(err.response?.data?.error || 'Failed to fetch user access report');
      console.error('Error fetching Cassandra users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectionChange = (e) => {
    const connId = e.target.value;
    setSelectedConnection(connId);
    setFilters(prev => ({ ...prev, collection_date: '' }));
    setCurrentPage(1);
    fetchCollectionDates(connId);
    fetchUsers(connId, 1, pageSize);
  };

  // Debounce timer for free-text search input — typed input must NOT fire a
  // request on every keystroke. Dropdown changes apply immediately.
  const searchDebounceTimer = React.useRef(null);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const nextFilters = { ...filters, [name]: value };
    setFilters(nextFilters);
    setCurrentPage(1);

    if (name === 'search') {
      // Debounce so we don't fire on every keystroke.
      if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
      searchDebounceTimer.current = setSafeTimeout(
        () => fetchUsers(selectedConnection, 1, pageSize, nextFilters),
        400
      );
    } else {
      // Auto-apply dropdowns immediately. Pass nextFilters explicitly because
      // setFilters above hasn't committed yet.
      fetchUsers(selectedConnection, 1, pageSize, nextFilters);
    }
  };

  const applyFilters = () => {
    // Kept for the Enter-key handler on the search input — flushes any pending
    // debounce immediately.
    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    setCurrentPage(1);
    fetchUsers(selectedConnection, 1, pageSize, filters);
  };

  const resetFilters = () => {
    const cleared = { collection_date: '', privilege_level: '', is_superuser: '', user_type: '', search: '' };
    setFilters(cleared);
    setCollectionDates([]);
    setCurrentPage(1);
    fetchUsers(selectedConnection, 1, pageSize, cleared);
  };

  const exportToCSV = async () => {
    try {
      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('cassandra-access/export/csv/'),
        { connection_id: selectedConnection, filters },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `cassandra_access_report_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting CSV:', err);
    }
  };

  // Server-side pagination: render whatever the server returned for this page.
  // startIndex/endIndex are display-only values for "Showing X-Y of N" copy.
  const currentUsers = users;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + users.length, totalCount);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      fetchUsers(selectedConnection, newPage, pageSize);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    fetchUsers(selectedConnection, 1, newSize);
  };

  const getRiskBadgeClass = (level) => {
    if (level >= 5) return 'text-danger fw-bold';
    if (level >= 4) return 'text-warning fw-bold';
    if (level >= 2) return 'text-info';
    return 'text-secondary';
  };

  // Shared pagination strip — rendered above AND below the table so users can
  // navigate without scrolling. `placement` toggles only the divider border.
  const renderPaginationBar = (placement) => {
    if (totalCount === 0) return null;
    const borderStyle = placement === 'top'
      ? { borderBottom: '1px solid #e5e7eb', marginBottom: '10px' }
      : { borderTop: '1px solid #e5e7eb', marginTop: '20px' };
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px',
        ...borderStyle,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label>Rows per page:</label>
          <select value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ marginLeft: '20px' }}>
            Showing {startIndex + 1}-{endIndex} of {totalCount}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '5px' }}>
          <button className="btn btn-secondary btn-small" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>First</button>
          <button className="btn btn-secondary btn-small" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
          <span style={{ padding: '0 10px', alignSelf: 'center' }}>Page {currentPage} of {totalPages}</span>
          <button className="btn btn-secondary btn-small" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
          <button className="btn btn-secondary btn-small" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>Last</button>
        </div>
      </div>
    );
  };

  const showPrivilegeDetails = async (user, privilegeType) => {
    // Fetch detailed user info with all permissions
    try {
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('cassandra-access/user-details/'), {
        connection_id: selectedConnection,
        username: user.username
      });

      const userData = response.data.user;
      const allPermissions = userData.all_permissions || [];

      // Filter permissions by type
      let filteredPermissions = [];

      if (privilegeType === 'roles') {
        setModalData({
          title: `Roles for ${user.username}`,
          type: 'roles',
          data: user.role_memberships || []
        });
      } else {
        // Filter permissions based on type
        filteredPermissions = allPermissions.filter(perm => {
          const resource = perm.resource || '';
          const permissions = perm.permissions || [];
          const isElevated = resource === 'data' || resource.startsWith('data/') ||
                           resource.toLowerCase().includes('<keyspace') ||
                           resource.toLowerCase().includes('<all');

          for (const p of permissions) {
            const pUpper = p.toUpperCase();

            if (privilegeType === 'admin' && pUpper === 'AUTHORIZE') return true;
            if (privilegeType === 'ddl' && ['CREATE', 'ALTER', 'DROP'].includes(pUpper)) return true;
            if (privilegeType === 'write_elevated' && ['MODIFY', 'EXECUTE'].includes(pUpper) && isElevated) return true;
            if (privilegeType === 'write' && ['MODIFY', 'EXECUTE'].includes(pUpper) && !isElevated) return true;
            if (privilegeType === 'read_elevated' && (['SELECT', 'DESCRIBE'].includes(pUpper) && isElevated)) return true;
            if (privilegeType === 'read' && pUpper === 'SELECT' && !isElevated) return true;
          }
          return false;
        });

        setModalData({
          title: `${privilegeType.toUpperCase().replace('_', ' ')} Privileges for ${user.username}`,
          type: privilegeType,
          data: filteredPermissions
        });
      }

      setShowModal(true);
    } catch (err) {
      console.error('Error fetching privilege details:', err);
      setError('Failed to fetch privilege details');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalData(null);
  };

  return (
    <div className="mysql-access-report">
      <div className="report-header">
        <h2>Cassandra User Access Report</h2>
        <p className="report-subtitle">Comprehensive role and permission analysis</p>
      </div>

      {/* Certification warning banner — mirrors Oracle / MSSQL / MySQL. Shown
          when the Cassandra Privilege Mappings still have unassigned or
          uncertified privileges; those don't contribute to a user's computed
          level, so the cards / table below may be understated. Hidden when
          everything is assigned + certified. */}
      {certStatus && (() => {
        const unassigned = (certStatus.unassigned_count || 0);
        const uncertPriv = (certStatus.uncertified_count || 0);
        const incomplete = unassigned + uncertPriv;
        if (incomplete === 0) return null;
        return (
          <div style={{
            background: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ color: '#856404', fontSize: '0.95rem', lineHeight: 1.5 }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
              <strong>Privilege levels below may be understated.</strong>
              {' '}The Cassandra privilege mappings are incomplete:
              {unassigned > 0 && <> <strong>{unassigned}</strong> unassigned privilege{unassigned === 1 ? '' : 's'}</>}
              {unassigned > 0 && uncertPriv > 0 ? ',' : ''}
              {uncertPriv > 0 && <> <strong>{uncertPriv}</strong> uncertified privilege{uncertPriv === 1 ? '' : 's'}</>}.
              {' '}Unassigned or uncertified mappings do not contribute to a user's
              computed privilege level until a DBA classifies and certifies them in{' '}
              <strong>Manage Privilege Mappings</strong>, so the levels shown here
              may be lower than a user's effective access.
            </div>
          </div>
        );
      })()}

      {/* Compact summary cards — scoped override so the shared .summary-card
          used by other tabs stays untouched. Cards listed severity high → low;
          tiers with zero users are hidden so the lineup reflects the data. */}
      <style>{`
        .cassandra-compact-cards {
          display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 16px;
        }
        .cassandra-compact-cards .summary-card {
          flex: 1 1 96px; padding: 6px 10px; border-radius: 7px;
          box-shadow: none; transition: none;
          display: flex; align-items: baseline; justify-content: space-between;
          gap: 8px;
        }
        .cassandra-compact-cards .summary-card:hover { transform: none; box-shadow: none; }
        .cassandra-compact-cards .summary-label {
          font-size: 10.5px; margin-bottom: 0; white-space: nowrap;
        }
        .cassandra-compact-cards .summary-value { font-size: 17px; }
      `}</style>
      {(() => {
        const SEVERITY_ORDER = [
          ['SUPER_ADMIN', 'danger'],
          ['ADMIN', 'danger'],
          ['WRITE_ELEVATED', 'warning'],
          ['WRITE', 'warning'],
          ['READ_ELEVATED', 'info'],
          ['READ', 'info'],
          ['NONE', ''],
          ['UNASSIGNED', ''],
        ];
        const dist = statistics.privilege_distribution || {};
        const levelCards = SEVERITY_ORDER
          .filter(([name]) => (dist[name] || 0) > 0)
          .map(([name, cls]) => ({ name, cls, count: dist[name] }));
        return (
          <div className="summary-cards cassandra-compact-cards">
            <div className="summary-card">
              <div className="summary-label">Total Users</div>
              <div className="summary-value">{statistics.total_users || 0}</div>
            </div>
            <div className="summary-card danger">
              <div className="summary-label">Superusers</div>
              <div className="summary-value">{statistics.superusers || 0}</div>
            </div>
            {levelCards.map(c => (
              <div key={c.name} className={`summary-card ${c.cls}`}>
                <div className="summary-label">{c.name.replace(/_/g, ' ')}</div>
                <div className="summary-value">{c.count}</div>
              </div>
            ))}
            <div className="summary-card info">
              <div className="summary-label">Total Roles</div>
              <div className="summary-value">{statistics.total_roles || 0}</div>
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Cassandra Cluster</label>
            <select value={selectedConnection} onChange={handleConnectionChange}>
              <option value="">Select Connection</option>
              {connections.map(conn => (
                <option key={conn.connection_id} value={conn.connection_id}>
                  {conn.connection_name} - {conn.contact_points}:{conn.port}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Collection Date</label>
            <select name="collection_date" value={filters.collection_date} onChange={handleFilterChange}>
              <option value="">Latest</option>
              {collectionDates.map((cd, idx) => (
                <option key={idx} value={cd.value}>{cd.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Privilege Level</label>
            <select name="privilege_level" value={filters.privilege_level} onChange={handleFilterChange}>
              <option value="">All Levels</option>
              {activeTiers.map(t => (
                <option key={t.tier_name} value={t.tier_value}>{t.tier_name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Superuser Status</label>
            <select name="is_superuser" value={filters.is_superuser} onChange={handleFilterChange}>
              <option value="">All Users</option>
              <option value="true">Superusers Only</option>
              <option value="false">Regular Users</option>
            </select>
          </div>

          <div className="filter-group">
            <label>User Type</label>
            <select name="user_type" value={filters.user_type} onChange={handleFilterChange}>
              <option value="">All Types</option>
              <option value="Internal">Internal</option>
              <option value="Individual">Individual</option>
              <option value="Security Group">Security Group</option>
              <option value="Application Service Account">Application Service Account</option>
              <option value="Unclassified">Unclassified</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Username Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search username..."
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            />
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={resetFilters}>Reset</button>
          </div>
        </div>
      </div>

      {/* Export Button */}
      <div className="export-section">
        <button className="btn btn-export" onClick={exportToCSV}>📊 Export to CSV</button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Users Table */}
      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="users-table-container">
          <h3>
            User Access Details
            {totalCount > 0
              ? ` (Showing ${startIndex + 1}-${endIndex} of ${totalCount} users)`
              : ` (0 users)`}
          </h3>
          {renderPaginationBar('top')}
          <div className="table-wrapper">
            <table className="table table-hover mb-0 users-table">
              <thead className="table-dark">
                <tr>
                  <th>Username</th>
                  <th>User Type</th>
                  <th>Is Superuser</th>
                  <th>Can Login</th>
                  <th>Roles</th>
                  <th>Admin</th>
                  <th>DDL</th>
                  <th>Write Elevated</th>
                  <th>Write</th>
                  <th>Read Elevated</th>
                  <th>Read</th>
                  <th>Privilege Level</th>
                  <th>Risk Assessment</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map((user, index) => (
                  <tr key={index} className={user.is_superuser ? 'critical-row' : ''}>
                    <td className="username-cell">{user.username}</td>
                    <td>
                      {user.user_type === 'Internal' ? (
                        <span className="text-secondary" title="System-managed by the database engine"><i className="fas fa-cog me-1" />Internal</span>
                      ) : user.user_type === 'Individual' ? (
                        <span className="text-success" title="Named user matched in enterprise directory"><i className="fas fa-user me-1" />Individual</span>
                      ) : user.user_type === 'Security Group' ? (
                        <span className="text-warning" title="Role or group principal (not a real user)"><i className="fas fa-users me-1" />Security Group</span>
                      ) : user.user_type === 'Application Service Account' ? (
                        <span className="text-info" title="Service account registered in account_owner with a designated attester"><i className="fas fa-cube me-1" />App Service</span>
                      ) : user.user_type === 'Unclassified' ? (
                        <span className="text-danger" title="Not in any registry — register in database_owners or account_owner"><i className="fas fa-question-circle me-1" />Unclassified</span>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td className={user.is_superuser ? 'critical-cell' : ''}>
                      <span style={{
                        color: user.is_superuser ? '#dc2626' : 'inherit',
                        fontWeight: user.is_superuser ? 'bold' : 'normal'
                      }}>
                        {user.is_superuser ? 'YES' : 'NO'}
                      </span>
                    </td>
                    <td>{user.can_login ? 'YES' : 'NO'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {user.roles_granted > 0 ? (
                        <a href="#" onClick={(e) => { e.preventDefault(); showPrivilegeDetails(user, 'roles'); }}
                           style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}>
                          {user.roles_granted}
                        </a>
                      ) : 0}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {user.admin_privs > 0 ? (
                        <a href="#" onClick={(e) => { e.preventDefault(); showPrivilegeDetails(user, 'admin'); }}
                           style={{ color: '#dc2626', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }}>
                          {user.admin_privs}
                        </a>
                      ) : 0}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {user.ddl_privs > 0 ? (
                        <a href="#" onClick={(e) => { e.preventDefault(); showPrivilegeDetails(user, 'ddl'); }}
                           style={{ color: '#dc2626', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }}>
                          {user.ddl_privs}
                        </a>
                      ) : 0}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {user.write_elevated_privs > 0 ? (
                        <a href="#" onClick={(e) => { e.preventDefault(); showPrivilegeDetails(user, 'write_elevated'); }}
                           style={{ color: '#ea580c', textDecoration: 'underline', cursor: 'pointer' }}>
                          {user.write_elevated_privs}
                        </a>
                      ) : 0}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {user.write_privs > 0 ? (
                        <a href="#" onClick={(e) => { e.preventDefault(); showPrivilegeDetails(user, 'write'); }}
                           style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}>
                          {user.write_privs}
                        </a>
                      ) : 0}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {user.read_elevated_privs > 0 ? (
                        <a href="#" onClick={(e) => { e.preventDefault(); showPrivilegeDetails(user, 'read_elevated'); }}
                           style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}>
                          {user.read_elevated_privs}
                        </a>
                      ) : 0}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {user.read_privs > 0 ? (
                        <a href="#" onClick={(e) => { e.preventDefault(); showPrivilegeDetails(user, 'read'); }}
                           style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}>
                          {user.read_privs}
                        </a>
                      ) : 0}
                    </td>
                    <td>
                      {/* Clickable: opens the per-(permission, resource)
                          breakdown that explains how this MAX-derived level
                          was computed. Same pattern as Oracle / MSSQL / MySQL. */}
                      <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none"
                        style={{ verticalAlign: 'baseline' }}
                        title="Show the grants behind this derived level"
                        onClick={() => openLevelBreakdown(user)}
                      >
                        <span className={getRiskBadgeClass(user.privilege_level)}>
                          {user.privilege_level_name || 'NONE'}
                        </span>
                        <i className="fas fa-up-right-from-square ms-1" style={{ fontSize: '0.65rem' }} />
                      </button>
                    </td>
                    <td>
                      <span className={getRiskBadgeClass(user.privilege_level)}>
                        {user.privilege_level >= 5 ? 'CRITICAL' :
                         user.privilege_level >= 4 ? 'HIGH' :
                         user.privilege_level >= 2 ? 'MEDIUM' : 'LOW'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {renderPaginationBar('bottom')}
        </div>
      )}

      {/* Modal for showing privilege details */}
      <WideModal
        show={showModal && !!modalData}
        onClose={closeModal}
        title={modalData?.title || ''}
        headerIcon="fa-key"
        maxWidth="800px"
        footer={<button className="btn btn-secondary" onClick={closeModal}>Close</button>}
      >
        {modalData && (modalData.type === 'roles' ? (
          <div>
            {modalData.data.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Role Name</th>
                    <th style={{ padding: '10px', textAlign: 'center', border: '1px solid #e5e7eb' }}>Depth</th>
                  </tr>
                </thead>
                <tbody>
                  {modalData.data.map((role, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '10px', border: '1px solid #e5e7eb' }}>{role.role}</td>
                      <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #e5e7eb' }}>{role.depth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No roles granted</p>
            )}
          </div>
        ) : (
          <div>
            {modalData.data.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Resource</th>
                    <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #e5e7eb' }}>Permissions</th>
                    <th style={{ padding: '10px', textAlign: 'center', border: '1px solid #e5e7eb' }}>Source</th>
                    <th style={{ padding: '10px', textAlign: 'center', border: '1px solid #e5e7eb' }}>Grant Type</th>
                  </tr>
                </thead>
                <tbody>
                  {modalData.data.map((perm, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '10px', border: '1px solid #e5e7eb', fontFamily: 'monospace', fontSize: '12px' }}>
                        {perm.resource}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #e5e7eb' }}>
                        {perm.permissions.join(', ')}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #e5e7eb' }}>
                        {perm.source}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #e5e7eb' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          backgroundColor: perm.grant_type === 'DIRECT' ? '#dbeafe' : '#fef3c7',
                          color: perm.grant_type === 'DIRECT' ? '#1e40af' : '#92400e',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          {perm.grant_type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No privileges of this type</p>
            )}
          </div>
        ))}
      </WideModal>

      {/* Privilege-level drill-down modal — shows EACH grant (permission +
          resource), the resolved scope and level, and which one(s) drive the
          stored MAX. Same shape as the Oracle / MSSQL / MySQL drill-downs so
          DBAs see the same explanation across techs. */}
      <WideModal
        show={!!levelBreakdown}
        onClose={() => setLevelBreakdown(null)}
        title={`Privilege Level — ${levelBreakdown?.name || ''}`}
        headerIcon="fa-calculator"
        maxWidth="1100px"
        footer={<button className="btn btn-secondary" onClick={() => setLevelBreakdown(null)}>Close</button>}
      >
        {levelBreakdown?.loading && (
          <div className="loading" style={{ padding: '20px' }}>Loading breakdown…</div>
        )}
        {levelBreakdown?.error && (
          <div className="error-message">{levelBreakdown.error}</div>
        )}
        {levelBreakdown?.data && (() => {
          const d = levelBreakdown.data;
          const stale = d.stored_level_name && d.stored_level_name !== d.derived_level_name;
          return (
            <div>
              <div style={{ marginBottom: '12px', fontSize: '0.9rem', color: '#555' }}>
                <strong>Derived level:</strong>{' '}
                <span className={getRiskBadgeClass(d.derived_level)}>{d.derived_level_name}</span>
                {d.is_superuser && <span className="ms-2 text-danger" title="Cassandra superuser flag">· superuser</span>}
                {d.matrix_capped && (
                  <span className="ms-2 text-warning" title={`Pre-clamp MAX was ${d.raw_max_level_name}, clamped by the matrix to ${d.matrix_cap_name}`}>
                    · matrix-clamped from {d.raw_max_level_name} to {d.matrix_cap_name}
                  </span>
                )}
                {stale && (
                  <span className="ms-2 text-warning" title="Stored level differs from a fresh recompute (run recategorize-users to sync)">
                    · stored: {d.stored_level_name} (stale)
                  </span>
                )}
                {' · '}{d.counts?.total || 0} components ({d.counts?.unknown || 0} unknown, {d.counts?.unassigned || 0} unassigned)
              </div>
              <table className="table table-sm table-bordered mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>Privilege</th>
                    <th>Resource</th>
                    <th>Scope</th>
                    <th>Grant</th>
                    <th>Source</th>
                    <th>Level</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.components || []).map((c, i) => (
                    <tr key={i} style={c.is_driver ? { backgroundColor: '#fff8c4' } : undefined}>
                      <td>{c.privilege}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{c.resource || '—'}</td>
                      <td>{c.scope}</td>
                      <td>{c.grant_type || '—'}</td>
                      <td>{c.source || '—'}</td>
                      <td>
                        <span className={getRiskBadgeClass(c.level)}>{c.level_name}</span>
                        {c.is_driver && <span className="ms-2 text-warning" title="Drives the derived MAX"><i className="fas fa-star" /></span>}
                        {c.unknown && <span className="ms-2 text-muted" title="Not in the reference table — contributes 0">unknown</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </WideModal>
    </div>
  );
};

export default CassandraUserAccessReport;
